import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type LldpRawRow, type Device } from '../api';

export function LldpPage({ canWrite }: { canWrite: boolean }) {
  const qc = useQueryClient();
  const [includeDismissed, setIncludeDismissed] = useState(false);
  const [includeLinked, setIncludeLinked] = useState(false);
  const [filter, setFilter] = useState('');
  const [converting, setConverting] = useState<LldpRawRow | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  const q = useQuery({
    queryKey: ['lldp-neighbors', includeDismissed, includeLinked],
    queryFn: () => api.lldpNeighbors({ includeDismissed, includeLinked })
  });

  const discover = useMutation({
    mutationFn: () => api.topologyDiscoverLldp(),
    onSuccess: (r: any) => {
      setMsg(`✓ ${r.devices} devices · ${r.ok} ok · ${r.neighbors} vizinhos · ${r.rawUpserts} upserts`);
      qc.invalidateQueries({ queryKey: ['lldp-neighbors'] });
    },
    onError: (e: any) => setMsg(`erro: ${e.detail?.detail ?? e.detail?.error ?? e.message ?? 'falhou'}`)
  });

  const dismiss = useMutation({
    mutationFn: ({ id, dismissed }: { id: number; dismissed: boolean }) => api.lldpDismiss(id, dismissed),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['lldp-neighbors'] })
  });

  const filtered = (q.data?.neighbors ?? []).filter((n) => {
    if (!filter) return true;
    const f = filter.toLowerCase();
    return (
      n.device.hostname.toLowerCase().includes(f) ||
      n.localIfaceName.toLowerCase().includes(f) ||
      (n.remoteSysName ?? '').toLowerCase().includes(f) ||
      (n.remoteIfaceName ?? '').toLowerCase().includes(f) ||
      (n.device.pop?.name ?? '').toLowerCase().includes(f)
    );
  });

  return (
    <section className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">LLDP — vizinhos descobertos</h1>
        {canWrite && (
          <div className="flex gap-2 items-center">
            {msg && <span className="text-xs text-slate-300">{msg}</span>}
            <button onClick={() => { setMsg(null); discover.mutate(); }} disabled={discover.isPending}
              className="px-3 py-1.5 text-sm bg-emerald-700 hover:bg-emerald-600 rounded disabled:opacity-50">
              {discover.isPending ? 'coletando…' : '⚡ Coletar LLDP agora'}
            </button>
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2 text-xs">
        <input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="filtrar por host/iface/sysName/POP…"
          className="bg-slate-950 ring-1 ring-slate-800 rounded px-3 py-1.5 flex-1 min-w-[14rem] max-w-md" />
        <label className="text-slate-400 flex items-center gap-1">
          <input type="checkbox" checked={includeDismissed} onChange={(e) => setIncludeDismissed(e.target.checked)} /> incluir dispensados
        </label>
        <label className="text-slate-400 flex items-center gap-1">
          <input type="checkbox" checked={includeLinked} onChange={(e) => setIncludeLinked(e.target.checked)} /> incluir já convertidos
        </label>
        <span className="text-slate-500 ml-auto">{filtered.length} de {q.data?.total ?? 0}</span>
      </div>

      {q.isLoading && <div className="text-slate-500 text-sm">carregando…</div>}
      {q.isError && <div className="text-rose-400 text-sm">erro: {(q.error as any)?.message}</div>}

      <div className="bg-slate-900 ring-1 ring-slate-800 rounded overflow-auto">
        <table className="w-full text-xs">
          <thead className="text-slate-400 bg-slate-800/60 sticky top-0">
            <tr className="border-b border-slate-800">
              <Th>POP</Th><Th>Device local</Th><Th>Iface local</Th><Th>SysName remoto</Th><Th>Iface remota</Th><Th>Mgmt IP</Th><Th>Match sugerido</Th><Th>Visto</Th><Th className="w-44">Ações</Th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((n) => (
              <tr key={n.id} className={`border-b border-slate-800 ${n.dismissed ? 'opacity-40' : ''} ${n.linkedToType ? 'bg-emerald-900/10' : ''} hover:bg-slate-800/30`}>
                <Td>{n.device.pop ? <span className="text-[10px]">{n.device.pop.uf && <code className="text-emerald-400 mr-1">{n.device.pop.uf}</code>}{n.device.pop.name}</span> : <span className="text-slate-600">—</span>}</Td>
                <Td className="font-mono">{n.device.hostname}</Td>
                <Td className="font-mono">{n.localIfaceName}</Td>
                <Td className="font-mono text-slate-300">{n.remoteSysName ?? <span className="text-slate-600">—</span>}</Td>
                <Td className="font-mono text-slate-400">{n.remoteIfaceName ?? '—'}</Td>
                <Td className="font-mono text-slate-500">{n.remoteMgmtIp ?? '—'}</Td>
                <Td>
                  {n.suggested
                    ? <span className="text-emerald-400 text-[10px]">→ {n.suggested.hostname} <span className="text-slate-500">({n.suggested.matchedBy})</span></span>
                    : <span className="text-slate-600 text-[10px]">sem match</span>}
                </Td>
                <Td className="text-[10px] text-slate-500">{new Date(n.lastSeen).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })}</Td>
                <Td>
                  {n.linkedToType ? (
                    <span className="text-emerald-400 text-[10px]">✓ {n.linkedToType}#{n.linkedToId}</span>
                  ) : canWrite ? (
                    <div className="flex gap-1">
                      <button onClick={() => setConverting(n)} className="px-1.5 py-0.5 text-[10px] rounded bg-emerald-700 hover:bg-emerald-600">+ link</button>
                      <button onClick={() => dismiss.mutate({ id: n.id, dismissed: !n.dismissed })} className="px-1.5 py-0.5 text-[10px] rounded bg-slate-700 hover:bg-slate-600">
                        {n.dismissed ? 'restaurar' : 'dispensar'}
                      </button>
                    </div>
                  ) : null}
                </Td>
              </tr>
            ))}
            {!filtered.length && (
              <tr><td colSpan={9} className="py-8 text-center text-slate-500">sem vizinhos {filter ? 'que correspondam ao filtro' : 'pendentes'}</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {converting && (
        <ConvertDialog raw={converting} onClose={() => setConverting(null)}
          onDone={() => { setConverting(null); qc.invalidateQueries({ queryKey: ['lldp-neighbors'] }); qc.invalidateQueries({ queryKey: ['pop-links'] }); qc.invalidateQueries({ queryKey: ['device-links'] }); }} />
      )}
    </section>
  );
}

function ConvertDialog({ raw, onClose, onDone }: { raw: LldpRawRow; onClose: () => void; onDone: () => void }) {
  const [targetDeviceId, setTargetDeviceId] = useState<number | ''>(raw.suggested?.deviceId ?? '');
  const [search, setSearch] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const devsQ = useQuery({ queryKey: ['devices', '', '', search], queryFn: () => api.devices({ q: search, take: 200 }) });

  const convert = useMutation({
    mutationFn: () => api.lldpConvert(raw.id, Number(targetDeviceId)),
    onSuccess: (r) => { alert(`✓ ${r.kind} #${r.link.id} criado`); onDone(); },
    onError: (e: any) => setErr(e.detail?.detail ?? e.detail?.error ?? e.message ?? 'erro')
  });

  const selected = (devsQ.data?.devices ?? []).find((d: Device) => d.id === targetDeviceId);
  const samePop = selected && raw.device.popId && selected.popId === raw.device.popId;

  return (
    <div className="fixed inset-0 bg-slate-950/85 z-30 flex items-center justify-center p-4">
      <div className="bg-slate-900 ring-1 ring-slate-800 rounded-lg p-5 w-full max-w-xl space-y-3">
        <h3 className="text-sm font-semibold">Converter vizinho LLDP em enlace</h3>

        <div className="text-xs text-slate-400 space-y-0.5">
          <div><span className="text-slate-500">Origem:</span> <span className="font-mono text-slate-200">{raw.device.hostname}</span> <span className="text-slate-500">·</span> <span className="font-mono">{raw.localIfaceName}</span></div>
          <div><span className="text-slate-500">Remoto declarado:</span> <span className="font-mono text-slate-200">{raw.remoteSysName ?? '—'}</span> <span className="text-slate-500">·</span> <span className="font-mono">{raw.remoteIfaceName ?? '—'}</span> {raw.remoteMgmtIp && <span className="text-slate-500"> · IP {raw.remoteMgmtIp}</span>}</div>
        </div>

        <div>
          <label className="block text-xs text-slate-400 mb-1">Buscar device de destino</label>
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="hostname/IP/desc…"
            className="w-full bg-slate-950 ring-1 ring-slate-800 rounded px-3 py-1.5 text-xs mb-2" />
          <select value={targetDeviceId} onChange={(e) => setTargetDeviceId(e.target.value ? Number(e.target.value) : '')}
            className="w-full bg-slate-950 ring-1 ring-slate-800 rounded px-2 py-1.5 text-xs">
            <option value="">— escolha um device —</option>
            {(devsQ.data?.devices ?? []).map((d: Device) => (
              <option key={d.id} value={d.id} disabled={d.id === raw.device.id}>
                {d.hostname} {d.pop ? ` [${d.pop.uf ?? ''} ${d.pop.name ?? ''}]` : ' (sem POP)'} {d.id === raw.device.id ? ' (origem — desabilitado)' : ''}
              </option>
            ))}
          </select>
        </div>

        {selected && (
          <div className={`text-xs px-2 py-1.5 rounded ${samePop ? 'bg-sky-900/30 text-sky-300' : 'bg-amber-900/30 text-amber-300'}`}>
            Será criado como <strong>{samePop ? 'DeviceLink (intra-POP)' : 'PopLink (inter-POP)'}</strong>
            {!selected.popId && ' — atenção: device de destino sem POP, vai falhar'}
          </div>
        )}

        {err && <div className="text-rose-400 text-xs">{err}</div>}

        <div className="flex justify-end gap-2 pt-2 border-t border-slate-800">
          <button onClick={onClose} className="px-3 py-1.5 text-xs text-slate-400 hover:text-slate-200">cancelar</button>
          <button onClick={() => { setErr(null); convert.mutate(); }} disabled={!targetDeviceId || convert.isPending}
            className="px-3 py-1.5 text-xs bg-emerald-700 hover:bg-emerald-600 rounded disabled:opacity-50">
            {convert.isPending ? 'criando…' : '✓ converter'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Th({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return <th className={`text-left px-2 py-1.5 font-medium ${className}`}>{children}</th>;
}
function Td({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return <td className={`px-2 py-1 ${className}`}>{children}</td>;
}
